import React from 'react';
import { cn } from '@/utils/cn';

export type BadgeSize = 's' | 'm' | 'l' | 'xl';
export type BadgeType =
  | 'white'
  | 'white-outline'
  | 'grey'
  | 'grey-outline'
  | 'green'
  | 'red'
  | 'amber';

export interface TeeBadgeProps {
  /**
   * The label text to display in the badge
   */
  label?: string;
  /**
   * The label number to display in the badge
   */
  labelNumber?: number;
  /**
   * The size of the badge
   */
  size?: BadgeSize;
  /**
   * The color type of the badge
   */
  type?: BadgeType;
  /**
   * Icon to display on the left side
   */
  leftIcon?: React.ReactNode;
  /**
   * Icon to display on the right side
   */
  rightIcon?: React.ReactNode;
  /**
   * Action element to display on the left side
   */
  leftAction?: React.ReactNode;
  /**
   * Action element to display on the right side
   */
  rightAction?: React.ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
}

const TeeBadge: React.FC<TeeBadgeProps> = ({
  label,
  labelNumber,
  size = 'm',
  type = 'white',
  leftIcon,
  rightIcon,
  leftAction,
  rightAction,
  className,
}) => {
  const sizeClasses = {
    s: 'px-[8px] py-[2px]',
    m: 'px-[8px] py-[2px]',
    l: 'px-[10px] py-[4px]',
    xl: 'px-[10px] py-[4px]',
  };

  const typeClasses = {
    white: 'bg-[var(--background-badge-white,#FFF)]',
    'white-outline':
      'bg-transparent border border-[var(--border-badge-white,#E6DFCF)]',
    grey: 'bg-[var(--background-badge-gray,#F5EEDE)]',
    'grey-outline':
      'bg-transparent border border-[var(--border-badge-gray,#D0D0D0)]',
    green: 'bg-[var(--background-badge-green,#C4DFCE)]',
    red: 'bg-[var(--background-badge-red,#F6C9C9)]',
    amber: 'bg-[var(--background-badge-amber,#F2E2C0)]',
  };

  const textClasses = {
    white: 'text-[var(--text-badge-default,#16241E)]',
    'white-outline': 'text-[var(--text-badge-default,#16241E)]',
    grey: 'text-[var(--text-badge-gray)]',
    'grey-outline': 'text-[var(--text-badge-gray)]',
    green: 'text-[var(--text-badge-green,#0C3D2D)]',
    red: 'text-[var(--text-badge-red,#923430)]',
    amber: 'text-[var(--text-badge-warning,#F57C00)]',
  };

  const textSizeClasses = {
    s: 'text-body-xs',
    m: 'text-body-s',
    l: 'text-body-m',
    xl: 'text-body-l',
  };

  const displayContent = () => {
    if (labelNumber !== undefined) {
      return labelNumber;
    }
    return label;
  };

  return (
    <div
      className={cn(
        'inline-flex shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-full font-medium',
        sizeClasses[size],
        typeClasses[type],
        textClasses[type],
        className
      )}
    >
      {leftAction && <div className="flex items-center">{leftAction}</div>}
      {leftIcon && <div className="flex items-center">{leftIcon}</div>}
      {displayContent() && (
        <span className={cn('font-regular', textSizeClasses[size])}>
          {displayContent()}
        </span>
      )}
      {rightIcon && <div className="flex items-center">{rightIcon}</div>}
      {rightAction && <div className="flex items-center">{rightAction}</div>}
    </div>
  );
};

TeeBadge.displayName = 'TeeBadge';

export default TeeBadge;
